import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Search, User as UserIcon, Shield, LogOut, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const PRIMARY = [
  { to: "/relampago", label: "Relámpago" },
  { to: "/encuestas", label: "Encuestas" },
  { to: "/resultados", label: "Resultados" },
  { to: "/propuestas", label: "Propuestas" },
];
const MORE = [
  { to: "/metodologia", label: "Metodología" },
  { to: "/sobre", label: "Sobre 2Voto" },
];
const NAV = [...PRIMARY, ...MORE];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(false);
  const [q, setQ] = useState("");
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user && ["admin", "superadmin", "editor", "moderator"].includes(user.role);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim().length >= 2) { navigate(`/buscar?q=${encodeURIComponent(q.trim())}`); setOpen(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-8">
            <Logo />
            <nav className="hidden lg:flex items-center gap-1">
              {PRIMARY.map((n) => (
                <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${location.pathname.startsWith(n.to) ? "text-cyan-600 bg-cyan-50" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
                  {n.label}
                </Link>
              ))}
              <div className="relative" onMouseEnter={() => setMore(true)} onMouseLeave={() => setMore(false)}>
                <button data-testid="nav-more" className="px-3 py-2 text-sm font-medium rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100 inline-flex items-center gap-1">
                  Más <ChevronDown className="w-4 h-4" />
                </button>
                {more && (
                  <div className="absolute left-0 top-full pt-1 w-48" data-testid="more-menu">
                    <div className="bg-white rounded-lg border border-slate-200 shadow-lg py-1">
                      {MORE.map((n) => (
                        <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
                          className="block px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-cyan-600">{n.label}</Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden md:flex items-center bg-slate-100 rounded-full px-3 py-1.5">
              <Search className="w-4 h-4 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…"
                data-testid="search-input"
                className="bg-transparent outline-none text-sm px-2 w-32 lg:w-40" />
            </form>
            {isAdmin && (
              <Link to="/admin" data-testid="nav-admin"
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 hover:text-cyan-600">
                <Shield className="w-4 h-4" /> CMS
              </Link>
            )}
            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/perfil" data-testid="nav-profile" className="hidden sm:inline-flex items-center gap-2 px-2 py-1.5 rounded-full hover:bg-slate-100">
                  {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full" /> : <UserIcon className="w-5 h-5 text-slate-500" />}
                  <span className="text-sm font-medium text-slate-700 max-w-[100px] truncate">{user.alias || user.name}</span>
                </Link>
                <button onClick={logout} data-testid="logout-button" className="p-2 text-slate-400 hover:text-rose-500" title="Cerrar sesión">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button onClick={login} data-testid="login-button"
                className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-slate-800 transition-colors">
                Crear cuenta
              </button>
            )}
            <button onClick={() => setOpen(!open)} className="lg:hidden p-2 text-slate-700" data-testid="mobile-menu-toggle">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1" data-testid="mobile-menu">
            <form onSubmit={submitSearch} className="flex items-center bg-slate-100 rounded-full px-3 py-2 mb-2">
              <Search className="w-4 h-4 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="bg-transparent outline-none text-sm px-2 flex-1" />
            </form>
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-100">{n.label}</Link>
            ))}
            {isAdmin && <Link to="/admin" onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-cyan-600 rounded-md hover:bg-slate-100">Panel CMS</Link>}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-slate-900 text-slate-300 mt-16">
        <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <Logo light />
            <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-xs">Tu voz después de votar. Tu primer voto elige. Tu segundo voto fiscaliza.</p>
            <p className="mt-4 text-xs text-slate-500">Plataforma ciudadana independiente · Piloto: Puerto Rico</p>
          </div>
          <div>
            <p className="font-heading font-bold text-white mb-3">Participa</p>
            <ul className="space-y-2 text-sm">
              {PRIMARY.map((n) => <li key={n.to}><Link to={n.to} className="hover:text-cyan-400">{n.label}</Link></li>)}
            </ul>
          </div>
          <div>
            <p className="font-heading font-bold text-white mb-3">Explora</p>
            <ul className="space-y-2 text-sm">
              {MORE.map((n) => <li key={n.to}><Link to={n.to} className="hover:text-cyan-400">{n.label}</Link></li>)}
            </ul>
          </div>
          <div>
            <p className="font-heading font-bold text-white mb-3">Principios</p>
            <ul className="space-y-2 text-sm text-slate-400">
              <li>Independencia de partidos</li>
              <li>Evidencia antes que acusaciones</li>
              <li>Derecho a réplica</li>
              <li>Transparencia metodológica</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 py-6 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} 2Voto — Tu voz después de votar.
        </div>
      </footer>
    </div>
  );
}
